/**
 * Client HTTP de l'API. Un seul point d'entrée pour que la gestion du jeton et
 * des erreurs soit identique partout : au comptoir, un message d'erreur clair
 * vaut mieux qu'un écran figé.
 */

const CLE_JETON = 'troc.jeton'

let jeton: string | null = localStorage.getItem(CLE_JETON)

export function definirJeton(valeur: string | null): void {
  jeton = valeur
  if (valeur) localStorage.setItem(CLE_JETON, valeur)
  else localStorage.removeItem(CLE_JETON)
}

export function jetonCourant(): string | null {
  return jeton
}

/**
 * L'auteur déclaré de l'action en cours.
 *
 * Quatre personnes se partagent l'iPad et personne ne se déconnecte : le
 * compte de la session ne dit donc pas qui encaisse. Chaque requête qui écrit
 * emporte, en plus, le vendeur choisi au moment de valider. C'est cet en-tête
 * que le journal enregistre.
 */
let vendeurActif: number | null = null

export function definirVendeurActif(id: number | null): void {
  vendeurActif = id
}

export function vendeurActifCourant(): number | null {
  return vendeurActif
}

export class ErreurApi extends Error {
  constructor(
    message: string,
    readonly statut: number,
    readonly donnees?: unknown,
  ) {
    super(message)
    this.name = 'ErreurApi'
  }
}

/** Émis quand le serveur refuse la session — la coquille repasse en connexion. */
export const evenements = new EventTarget()

interface Options {
  methode?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  corps?: unknown
  signal?: AbortSignal
  /** En-têtes supplémentaires — le laissez-passer gérant du Bilan, notamment. */
  entetes?: Record<string, string>
}

export async function api<T>(chemin: string, options: Options = {}): Promise<T> {
  const entetes: Record<string, string> = { ...options.entetes }
  if (jeton) entetes.authorization = `Bearer ${jeton}`
  if (vendeurActif !== null) entetes['x-vendeur'] = String(vendeurActif)
  if (options.corps !== undefined) entetes['content-type'] = 'application/json'

  let reponse: Response
  try {
    reponse = await fetch(`/api${chemin}`, {
      method: options.methode ?? 'GET',
      headers: entetes,
      body: options.corps === undefined ? undefined : JSON.stringify(options.corps),
      signal: options.signal,
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ErreurApi('Serveur injoignable — vérifiez le réseau de la boutique.', 0)
  }

  if (reponse.status === 401) {
    definirJeton(null)
    evenements.dispatchEvent(new Event('session-expiree'))
    throw new ErreurApi('Session expirée — reconnectez-vous.', 401)
  }

  const texte = await reponse.text()
  const donnees = texte ? (JSON.parse(texte) as unknown) : null

  if (!reponse.ok) {
    const message =
      donnees && typeof donnees === 'object' && 'erreur' in donnees
        ? String((donnees as { erreur: unknown }).erreur)
        : `Erreur ${reponse.status}`
    throw new ErreurApi(message, reponse.status, donnees)
  }

  return donnees as T
}
